<template>
  <v-expansion-panels v-model="outerPanel" accordion class="narrow-expansion-panels">
    <v-expansion-panel>
      <v-expansion-panel-header>
        <span class="subtitle-2">
          {{ segments.length }} {{ segments.length === 1 ? $t('segment') : $t('segments') }}
        </span>
      </v-expansion-panel-header>
      <v-expansion-panel-content>
        <DataTable
          :tableElementId="'ticket-scale-data-segment-table'"
          expandable
          smallHeaders
          containerClass="px-1"
          dense
          disablePagination
          hideHeader
          :expandIcon="'mdi-chevron-down'"
          expandOnRowClick
          :tableKey="'uuid'"
          :items="segments"
          :headers="segmentHeadersGroupedBySegmentFiltered"
          :customCells="customCells"
          :tableHeightCalculationData="{ offset: 300 }"
          :itemCanExpandFunction="itemCanExpand"
          @expanded="onExpanded"
          >
          <template #attributes="{ item: segment }">
            <v-row no-gutters>
              <v-col cols="auto">
                <v-row justify="center">
                  <Icon iconColor="warning" v-if="segmentUsesBaseRate(segment)" icon="baseRateMop" :tooltipText="$t('baseRateUsed')" :small="false" margin="mr-n5"></Icon>
                  <Icon iconColor="grey darken-2" v-if="showButtLogIndicator(segment)" :icon="'butt-log'" :tooltipText="$t('buttLog')" :small="false" margin="mr-n5"></Icon>
                </v-row>
              </v-col>
            </v-row>
          </template>

          <template #cut="{ item: segment }">
            <v-chip v-for="chip in cutChipsFor(segment)" :key="chip.id" small style="margin-top: 2px; margin-bottom: 2px; margin-right: 4px;">
              <Icon :icon="chip.icon" :tooltipText="$t(chip.id)" iconColor="grey darken-2"></Icon>
              <span>
                {{ chip.text }}
              </span>
            </v-chip>
          </template>

          <template #amounts="{ item: segment }">
            <v-icon v-if="segment.amounts.length > 0">{{ `mdi-chevron-${expandedSegments.includes(segment) ? 'up' : 'down'}` }}</v-icon>
          </template>

          <template #defectDefinition="{ item: segment }">
            <span v-if="segment.defectDefinition"> {{ segment.defectDefinition }} </span>
          </template>

          <template #expanded-item="{ item: segment }">
            <td
              class="grey lighten-3"
              colspan="100%"
              style="border-bottom: 1px solid #e0e0e0 !important;">
              <TicketScaleDataFinancials
                :segmentOrLog="segment"
                :rateSheets="rateSheets"
                isExpandedTableRow
                @rate-sheet-detail-clicked="rateSheetDetailClicked"/>
            </td>
          </template>
        </DataTable>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
import { baseRateMop } from '@/images/AngelSvgLibrary.js'

export default {
  name: 'TicketScaleDataBySegment',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    TicketScaleDataFinancials: () => import('@/components/ticket/ticket-details/scale-data/TicketScaleDataFinancials.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue')
  },

  props: {
    segments: {
      type: Array,
      required: true
    },
    segmentHeadersGroupedBySegment: {
      type: Array,
      required: true
    },
    rateSheets: {
      type: Map,
      required: true
    }
  },

  data: () => ({
    baseRateMop,
    expandedSegments: [],
    outerPanel: null
  }),

  created () {
    if (this.segments && this.segments.some(this.segmentUsesBaseRate)) {
      this.outerPanel = 0
    } else {
      this.outerPanel = null
    }
  },

  computed: {
    customCells () {
      return [
        {
          slotName: 'attributes',
          value: 'attributes'
        },
        {
          slotName: 'cut',
          value: 'cut'
        },
        {
          slotName: 'amounts',
          value: 'amounts'
        },
        {
          slotName: 'defectDefinition',
          value: 'defectDefinition'
        }
      ]
    },

    segmentHeadersGroupedBySegmentFiltered () {
      return this.segmentHeadersGroupedBySegment.filter(header => {
        switch (header.value) {
          case 'attributes': {
            return this.segments.some(s => s.buttLog) || this.segments.some(s => this.segmentUsesBaseRate(s))
          }
          case 'grade': {
            return this.segments.some(s => !!s.grade)
          }
          case 'defectDefinition': {
            return this.segments.some(s => !!s.defectDefinitionId)
          }
          case 'cut': {
            return this.segments.some(s => !!s.defectDefinitionId)
          }
          default: return true
        }
      })
    }
  },

  methods: {
    rateSheetDetailClicked (tableRow) {
      this.$emit('rate-sheet-detail-clicked', { tableRow, segments: this.expandedSegments })
    },

    cutChipsFor (segment) {
      const chips = []
      if (segment.lengthCut) chips.push({ id: 'lengthCut', icon: 'mdi-ruler', text: segment.lengthCut })
      if (segment.diameterCut) chips.push({ id: 'diameterCut', icon: 'mdi-diameter', text: segment.diameterCut })
      if (segment.volumeCut) chips.push({ id: 'volumeCut', icon: 'mdi-cube', text: segment.volumeCut })
      return chips
    },

    showButtLogIndicator (item) {
      return item.buttLog && item.segmentNumber === 2
    },

    itemCanExpand (item) {
      return item.amounts?.length > 0
    },

    onExpanded (expandedItems) {
      this.expandedSegments = expandedItems
    },

    segmentUsesBaseRate (segment) {
      return segment.amounts?.some(a => a.rateSheetLineId === -1)
    }
  }
}
</script>
